/*
 * Copyright (C) 2024 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */

import React from 'react'

import {useScope as createI18nScope} from '@canvas/i18n'
import {Button} from '@instructure/ui-buttons'
import {Flex} from '@instructure/ui-flex'
import {Heading} from '@instructure/ui-heading'
import {Tabs} from '@instructure/ui-tabs'
import {SimpleSelect} from '@instructure/ui-simple-select'
import {Text} from '@instructure/ui-text'
import {Outlet, useMatch, useNavigate} from 'react-router-dom'
import {openRegistrationWizard} from '../manage/registration_wizard/RegistrationWizardModalState'
import {useMedia} from 'react-use'
import {View} from '@instructure/ui-view'
import {LtiRegistrationsTab} from './constants'
import {isLtiRegistrationsUsageEnabled} from '../monitor/utils'
import {refreshRegistrations} from '../manage/api/registrations'
const I18n = createI18nScope('lti_registrations')

export const LtiAppsLayout = React.memo(() => {
  const isManage = useMatch('/manage/*')
  const isMonitor = useMatch('/monitor/*')

  const navigate = useNavigate()
  const isMobile = useMedia('(max-width: 767px)')

  const isSubAccount = React.useMemo(() => {
    return window.ENV.ACCOUNT_GLOBAL_ID !== window.ENV.DOMAIN_ROOT_ACCOUNT_ID
  }, [])

  const tabSelected = React.useMemo(() => {
    // If we're in a sub-account, force the monitor tab
    if (isSubAccount) {
      return LtiRegistrationsTab.monitor
    }

    return isManage
      ? LtiRegistrationsTab.manage
      : isMonitor
        ? LtiRegistrationsTab.monitor
        : LtiRegistrationsTab.discover
  }, [isManage, isMonitor, isSubAccount])

  const {isTabManage, isTabDiscover, isTabMonitor} = React.useMemo(() => {
    return {
      isTabManage: tabSelected === LtiRegistrationsTab.manage,
      isTabMonitor: tabSelected === LtiRegistrationsTab.monitor,
      isTabDiscover: tabSelected === LtiRegistrationsTab.discover,
    }
  }, [tabSelected])

  const onTabClick = React.useCallback(
    (_: any, tab: {id?: string}) => {
      if (isSubAccount) {
        navigate('/monitor')
      } else {
        switch (tab.id) {
          case LtiRegistrationsTab.discover:
            navigate('/')
            break
          case LtiRegistrationsTab.manage:
            navigate('/manage')
            break
          case LtiRegistrationsTab.monitor:
            navigate('/monitor')
            break
          default:
            navigate(isSubAccount ? '/monitor' : '/')
            break
        }
      }
    },
    [navigate, isSubAccount],
  )

  // Redirect sub-accounts to monitor tab if they're on discover or manage
  React.useEffect(() => {
    if (isSubAccount && !isMonitor) {
      navigate('/monitor', {replace: true})
    }
  }, [isSubAccount, isMonitor, navigate])

  const open = React.useCallback(() => {
    openRegistrationWizard({
      jsonUrl: '',
      jsonCode: '',
      unifiedToolId: undefined,
      dynamicRegistrationUrl: '',
      lti_version: '1p3',
      method: 'dynamic_registration',
      registering: false,
      onSuccessfulInstallation: registrationId => {
        refreshRegistrations()
        if (window.ENV.FEATURES.lti_registrations_next) {
          navigate(`/manage/${registrationId}`)
        }
      },
      jsonFetch: {_tag: 'initial'},
    })
  }, [navigate])

  return (
    <>
      <Flex alignItems="start" justifyItems="space-between" margin="0 0 small 0">
        <Flex.Item>
          <Flex alignItems="center">
            <Flex.Item>
              <Heading level="h1">{I18n.t('Apps')}</Heading>
            </Flex.Item>
          </Flex>
        </Flex.Item>
        {isManage && !isSubAccount ? (
          <Flex.Item>
            <Button color="primary" onClick={open} id="install-new-lti-app">
              {I18n.t('Install a New App')}
            </Button>
          </Flex.Item>
        ) : null}
      </Flex>
      <Text>
        {ENV.FEATURES.canvas_apps_sub_account_access
          ? I18n.t(
              'The Canvas Apps page lets root account administrators discover, install, and oversee integrated applications, while sub-account administrators can monitor usage.',
            )
          : I18n.t(
              'Apps is the central hub to discover, manage, and monitor integrated apps. Extend and enhance your digital teaching and learning experience with powerful apps that provide and/or enrich your content, assessment, multimedia, collaboration, analytics, accessibility, and more. Select Discover to explore and install new apps, Manage to review and manage installed apps, and Monitor to view and understand usage.',
            )}
      </Text>
      {isMobile ? (
        <>
          <View margin="small 0" display="block">
            <SimpleSelect renderLabel="" onChange={onTabClick} value={tabSelected}>
              {!isSubAccount && (
                <SimpleSelect.Option id="discover" value="discover">
                  {I18n.t('Discover')}
                </SimpleSelect.Option>
              )}
              {!isSubAccount && (
                <SimpleSelect.Option id="manage" value="manage">
                  {I18n.t('Manage')}
                </SimpleSelect.Option>
              )}
              {isLtiRegistrationsUsageEnabled() && (
                <SimpleSelect.Option id="monitor" value="monitor">
                  {I18n.t('Monitor')}
                </SimpleSelect.Option>
              )}
            </SimpleSelect>
          </View>
          <Outlet />
        </>
      ) : (
        <Tabs margin="medium auto" padding="medium" onRequestTabChange={onTabClick}>
          {!isSubAccount && (
            <Tabs.Panel
              renderTitle={
                <Text style={{color: 'initial', textDecoration: 'initial'}}>
                  {I18n.t('Discover')}
                </Text>
              }
              id={LtiRegistrationsTab.discover}
              active={isTabDiscover}
              isSelected={isTabDiscover}
              padding="large 0"
              href="/"
              themeOverride={{defaultOverflowY: 'unset'}}
            >
              <Outlet />
            </Tabs.Panel>
          )}
          {!isSubAccount && (
            <Tabs.Panel
              renderTitle={
                <Text style={{color: 'initial', textDecoration: 'initial'}}>
                  {I18n.t('Manage')}
                </Text>
              }
              id={LtiRegistrationsTab.manage}
              padding="large x-small"
              active={isTabManage}
              isSelected={isTabManage}
              themeOverride={{defaultOverflowY: 'unset'}}
            >
              <Outlet />
            </Tabs.Panel>
          )}
          {isLtiRegistrationsUsageEnabled() ? (
            <Tabs.Panel
              renderTitle={
                <Text style={{color: 'initial', textDecoration: 'initial'}}>
                  {I18n.t('Monitor')}
                </Text>
              }
              id={LtiRegistrationsTab.monitor}
              active={isTabMonitor}
              isSelected={isTabMonitor}
              themeOverride={{defaultOverflowY: 'unset'}}
            >
              <Outlet />
            </Tabs.Panel>
          ) : undefined}
        </Tabs>
      )}
    </>
  )
})
